import { ButtonHTMLAttributes, type Ref } from "react";
import { cx } from "./classNames.js";

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";
export type ButtonSize = "xs" | "sm" | "md";

const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  primary:
    "bg-arch-600 text-white hover:bg-arch-500 focus-visible:ring-arch-400",
  secondary:
    "bg-slate-700 text-slate-100 hover:bg-slate-600 focus-visible:ring-slate-400",
  ghost:
    "bg-transparent text-slate-300 hover:bg-slate-800 hover:text-slate-100 focus-visible:ring-slate-500",
  danger:
    "bg-red-700 text-white hover:bg-red-600 focus-visible:ring-red-400",
};

const SIZE_CLASSES: Record<ButtonSize, string> = {
  // `xs` exists for the dense surfaces — process tree rows, view builder chips,
  // board chrome — where an `sm` button would dominate the row it sits in.
  xs: "h-6 px-2 text-[11px]",
  sm: "h-7 px-2.5 text-xs",
  md: "h-9 px-4 text-sm",
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  readonly variant?: ButtonVariant;
  readonly size?: ButtonSize;
  /** React 19 forwards refs as a plain prop — anchor menus/tooltips off it. */
  readonly ref?: Ref<HTMLButtonElement>;
}

/**
 * The canonical button class string — base + focus ring + disabled + variant +
 * size. Exported so a non-`<Button>` trigger that must LOOK like a button (e.g. a
 * menu anchor that owns its own ref) reuses the exact same classes instead of
 * re-assembling them (the drift {@link Button} exists to kill).
 */
export function buttonClasses(
  variant: ButtonVariant = "secondary",
  size: ButtonSize = "md",
  className?: string,
): string {
  return cx(
    "inline-flex items-center justify-center gap-1.5 rounded-md font-medium",
    "transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950",
    "disabled:cursor-not-allowed disabled:opacity-50",
    VARIANT_CLASSES[variant],
    SIZE_CLASSES[size],
    className,
  );
}

/**
 * The canonical labeled button — DESIGN-SYSTEM.md §1. One variant (by intent),
 * one size. Base + focus ring + disabled are mandatory and uniform; only the
 * ring color and background vary by variant.
 */
export function Button({
  variant = "secondary",
  size = "md",
  className,
  children,
  ref,
  ...rest
}: ButtonProps) {
  return (
    <button
      ref={ref}
      type="button"
      className={buttonClasses(variant, size, className)}
      {...rest}
    >
      {children}
    </button>
  );
}
